import React, { useState, useEffect } from 'react';
import importacaoService from '../services/importacaoService';
import { useFinanceiroStore } from '../store/financeiroStore';
import toast from 'react-hot-toast';
import { handleApiError } from '../services/errorHandler';
import { FiTrendingUp, FiDollarSign, FiPercent, FiTrash2, FiCalendar, FiBox, FiClock, FiCreditCard } from 'react-icons/fi';
import UploadInvoiceAI from './UploadInvoiceAI';

export default function ImportacaoForm({ onImportacaoCriada, importacaoParaEditar }) {
  const resultadoSimulacao = useFinanceiroStore((state) => state.resultadoSimulacao);
  const isSimulando = useFinanceiroStore((state) => state.isSimulando);
  const executarSimulacao = useFinanceiroStore((state) => state.executarSimulacao);
  const limparSimulacao = useFinanceiroStore((state) => state.limparSimulacao);

  const [formData, setFormData] = useState({
    dataImportacao: new Date().toISOString().split('T')[0],
    fornecedor: '',
    produto: '',
    sku: '',
    moeda: 'USD',
    
    dataInvoice: '',
    dataPagamento30: '',
    previsaoEmbarque: '',
    dataPagamento70: '',
    previsaoChegada: '',
    
    valorInicial: '',
    saldoRestante: '',
    taxaCambioEntrada: '',
    custoDesembaraco: '',
    
    possuiFinimp30: false,
    bancoFinimp30: '',
    dataContratacaoFinimp30: '',
    dataQuitacaoFinimp30: '',
    taxaJurosMensal30: '',
    prazoFinimp30Dias: '',
    
    possuiFinimp70: false,
    bancoFinimp70: '',
    dataContratacaoFinimp70: '',
    dataQuitacaoFinimp70: '',
    taxaJurosMensal70: '',
    prazoFinimp70Dias: '',
    
    observacoes: ''
  });

  const [loading, setLoading] = useState(false);

  // 🛠️ FUNÇÃO AUXILIAR: Formata data para Input HTML (YYYY-MM-DD)
  const formatarParaInput = (data) => {
    if (!data) return '';
    try {
      const d = new Date(data);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  // 🧽 FUNÇÃO AUXILIAR: Limpa formatação financeira (ex: 159.649,76 -> 159649.76)
  const tratarNumeroAduaneiro = (valor) => {
    if (valor === undefined || valor === null || valor === '') return '';
    if (typeof valor === 'number') return String(valor);
    
    let texto = String(valor).trim();
    texto = texto.replace(/[^\d.,-]/g, '');
    
    if (texto.includes('.') && texto.includes(',')) {
      texto = texto.replace(/\./g, ''); 
      texto = texto.replace(',', '.');  
    } 
    else if (texto.includes(',')) {
      texto = texto.replace(',', '.');
    }
    
    return texto;
  };

  // 🤖 PREENCHE O FORMULÁRIO COM OS DADOS DA IA
  const handleDadosIA = (dadosIA) => {
    setFormData(prev => {
      let novaMoeda = prev.moeda;
      if (dadosIA.moeda) {
        const moedaFormatada = dadosIA.moeda.toUpperCase().trim();
        if (['USD', 'EUR', 'CNY', 'BRL'].includes(moedaFormatada)) {
          novaMoeda = moedaFormatada;
        }
      }

      const fobLimpo = tratarNumeroAduaneiro(dadosIA.valorInicial);
      const saldoLimpo = tratarNumeroAduaneiro(dadosIA.saldoRestante);

      return {
        ...prev,
        fornecedor: dadosIA.fornecedor || prev.fornecedor,
        produto: dadosIA.produtos || prev.produto,
        valorInicial: fobLimpo || prev.valorInicial,
        saldoRestante: saldoLimpo || prev.saldoRestante,
        dataInvoice: formatarParaInput(dadosIA.dataInvoice) || prev.dataInvoice,
        dataPagamento30: formatarParaInput(dadosIA.dataPagamento30) || prev.dataPagamento30,
        previsaoEmbarque: formatarParaInput(dadosIA.previsaoEmbarque) || prev.previsaoEmbarque,
        dataPagamento70: formatarParaInput(dadosIA.dataPagamento70) || prev.dataPagamento70,
        previsaoChegada: formatarParaInput(dadosIA.previsaoChegada) || prev.previsaoChegada,
        moeda: novaMoeda
      };
    });
    
    toast.success('Dados da Invoice processados com sucesso!');
  };

  const handleInvoiceChange = (e) => {
    const dataSelecionada = e.target.value;
    
    if (dataSelecionada) {
      const [ano, mes, dia] = dataSelecionada.split('-');
      
      const embarque = new Date(ano, mes - 1, dia);
      embarque.setDate(embarque.getDate() + 50);

      const pag70 = new Date(embarque);
      pag70.setDate(pag70.getDate() + 60);

      const chegada = new Date(ano, mes - 1, dia);
      chegada.setDate(chegada.getDate() + 100);

      setFormData(prev => ({
        ...prev,
        dataInvoice: dataSelecionada,
        dataPagamento30: dataSelecionada,
        previsaoEmbarque: embarque.toISOString().split('T')[0],
        dataPagamento70: pag70.toISOString().split('T')[0],
        previsaoChegada: chegada.toISOString().split('T')[0]
      }));
    } else {
      setFormData(prev => ({ ...prev, dataInvoice: '' }));
    }
  };

  useEffect(() => {
    if (importacaoParaEditar) {
      setFormData({
        dataImportacao: formatarParaInput(importacaoParaEditar.dataImportacao),
        fornecedor: importacaoParaEditar.fornecedor || '',
        produto: importacaoParaEditar.produto || '',
        sku: importacaoParaEditar.sku || '',
        moeda: importacaoParaEditar.moeda || 'USD',
        
        dataInvoice: formatarParaInput(importacaoParaEditar.dataInvoice),
        dataPagamento30: formatarParaInput(importacaoParaEditar.dataPagamento30),
        previsaoEmbarque: formatarParaInput(importacaoParaEditar.previsaoEmbarque),
        dataPagamento70: formatarParaInput(importacaoParaEditar.dataPagamento70),
        previsaoChegada: formatarParaInput(importacaoParaEditar.previsaoChegada),
        
        valorInicial: importacaoParaEditar.valorInicial || '',
        saldoRestante: importacaoParaEditar.saldoRestante || '',
        taxaCambioEntrada: importacaoParaEditar.taxaCambioEntrada || '',
        custoDesembaraco: importacaoParaEditar.custoDesembaraco || '',
        
        possuiFinimp30: importacaoParaEditar.possuiFinimp30 || false,
        bancoFinimp30: importacaoParaEditar.bancoFinimp30 || '',
        dataContratacaoFinimp30: formatarParaInput(importacaoParaEditar.dataContratacaoFinimp30),
        dataQuitacaoFinimp30: formatarParaInput(importacaoParaEditar.dataQuitacaoFinimp30),
        taxaJurosMensal30: importacaoParaEditar.taxaJurosMensal30 || '',
        prazoFinimp30Dias: importacaoParaEditar.prazoFinimp30Dias || '',

        possuiFinimp70: importacaoParaEditar.possuiFinimp70 || false,
        bancoFinimp70: importacaoParaEditar.bancoFinimp70 || '',
        dataContratacaoFinimp70: formatarParaInput(importacaoParaEditar.dataContratacaoFinimp70),
        dataQuitacaoFinimp70: formatarParaInput(importacaoParaEditar.dataQuitacaoFinimp70),
        taxaJurosMensal70: importacaoParaEditar.taxaJurosMensal70 || '',
        prazoFinimp70Dias: importacaoParaEditar.prazoFinimp70Dias || '',
        
        observacoes: importacaoParaEditar.observacoes || ''
      });
    }
  }, [importacaoParaEditar]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.valorInicial && formData.taxaCambioEntrada) {
        executarSimulacao({
          valorFobUs: formData.valorInicial,
          saldoFobUs: formData.saldoRestante,
          cotacaoManual: formData.taxaCambioEntrada,
          custoDesembaracoBrl: formData.custoDesembaraco,
          possuiFinimp30: formData.possuiFinimp30,
          taxaJurosMensal30: formData.possuiFinimp30 ? formData.taxaJurosMensal30 : 0,
          prazoFinimp30Dias: formData.possuiFinimp30 ? formData.prazoFinimp30Dias : 0,
          possuiFinimp70: formData.possuiFinimp70,
          taxaJurosMensal70: formData.possuiFinimp70 ? formData.taxaJurosMensal70 : 0,
          prazoFinimp70Dias: formData.possuiFinimp70 ? formData.prazoFinimp70Dias : 0,
        });
      } else {
        limparSimulacao();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    formData.valorInicial,
    formData.saldoRestante,
    formData.taxaCambioEntrada,
    formData.custoDesembaraco,
    formData.possuiFinimp30,
    formData.taxaJurosMensal30,
    formData.prazoFinimp30Dias,
    formData.possuiFinimp70,
    formData.taxaJurosMensal70,
    formData.prazoFinimp70Dias,
    executarSimulacao,
    limparSimulacao
  ]);

  // ⚡ AUTOMATIZAÇÃO DAS DATAS DE QUITAÇÃO DO FINIMP
  useEffect(() => {
    setFormData((prev) => {
      let mudou = false;
      let novoDataQuitacao30 = prev.dataQuitacaoFinimp30;
      let novoDataQuitacao70 = prev.dataQuitacaoFinimp70;

      // Cálculo Finimp 30%
      if (prev.dataContratacaoFinimp30 && prev.prazoFinimp30Dias) {
        const d = new Date(prev.dataContratacaoFinimp30);
        if (!isNaN(d.getTime())) {
          d.setUTCDate(d.getUTCDate() + Number(prev.prazoFinimp30Dias));
          const quitacaoCalculada = d.toISOString().split('T')[0];
          
          if (quitacaoCalculada !== novoDataQuitacao30) {
            novoDataQuitacao30 = quitacaoCalculada;
            mudou = true;
          }
        }
      }

      // Cálculo Finimp 70%
      if (prev.dataContratacaoFinimp70 && prev.prazoFinimp70Dias) {
        const d = new Date(prev.dataContratacaoFinimp70);
        if (!isNaN(d.getTime())) {
          d.setUTCDate(d.getUTCDate() + Number(prev.prazoFinimp70Dias));
          const quitacaoCalculada = d.toISOString().split('T')[0];
          
          if (quitacaoCalculada !== novoDataQuitacao70) {
            novoDataQuitacao70 = quitacaoCalculada;
            mudou = true;
          }
        }
      }

      if (mudou) {
        return {
          ...prev,
          dataQuitacaoFinimp30: novoDataQuitacao30,
          dataQuitacaoFinimp70: novoDataQuitacao70
        };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.dataContratacaoFinimp30,
    formData.prazoFinimp30Dias,
    formData.dataContratacaoFinimp70,
    formData.prazoFinimp70Dias
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const valorFobNum = Number(formData.valorInicial) || 0;
      const taxaCambioNum = Number(formData.taxaCambioEntrada) || 0;
      const custoDesembaracoDefault = valorFobNum * taxaCambioNum * 0.50;

      const payload = {
        ...formData,
        valorInicial: valorFobNum,
        saldoRestante: formData.saldoRestante ? Number(formData.saldoRestante) : (valorFobNum * 0.70),
        taxaCambioEntrada: taxaCambioNum,
        custoDesembaraco: formData.custoDesembaraco ? Number(formData.custoDesembaraco) : custoDesembaracoDefault,
        taxaJurosMensal30: formData.possuiFinimp30 ? Number(formData.taxaJurosMensal30) : null,
        prazoFinimp30Dias: formData.possuiFinimp30 ? Number(formData.prazoFinimp30Dias) : null,
        taxaJurosMensal70: formData.possuiFinimp70 ? Number(formData.taxaJurosMensal70) : null,
        prazoFinimp70Dias: formData.possuiFinimp70 ? Number(formData.prazoFinimp70Dias) : null,
      };

      if (importacaoParaEditar) {
        await importacaoService.atualizar(importacaoParaEditar.id, payload);
        toast.success('Importação atualizada!');
      } else {
        await importacaoService.criar(payload);
        toast.success('Importação criada com sucesso!');
      }
      onImportacaoCriada();
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async () => {
    if (window.confirm(`Tem certeza que deseja excluir permanentemente a importação "${importacaoParaEditar.produto}"?`)) {
      setLoading(true);
      try {
        await importacaoService.deletar(importacaoParaEditar.id);
        toast.success('Importação excluída com sucesso!');
        onImportacaoCriada();
      } catch (error) {
        toast.error('Erro ao excluir. Verifique se existem pagamentos vinculados.');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatarMoeda = (valor) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const valorFobNum = Number(formData.valorInicial) || 0;
  const saldoRestanteUs = formData.saldoRestante ? Number(formData.saldoRestante) : (valorFobNum * 0.7);
  const taxaCambioNum = Number(formData.taxaCambioEntrada) || 0;
  const principalFinimpBrl = saldoRestanteUs * taxaCambioNum;
  const jurosTotalBrl = resultadoSimulacao?.jurosTotal || 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in duration-300">

      {!importacaoParaEditar && (
        <UploadInvoiceAI onDadosExtraidos={handleDadosIA} />
      )}

      {/* 📦 1. BLoco Dados da Operação */}
      <div className="bg-gray-50/80 p-6 rounded-2xl border border-gray-200 shadow-sm">
        <h3 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
          <FiBox className="text-blue-500" /> 1. Dados da Operação
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Registro no Sistema</label>
            <input type="date" required value={formData.dataImportacao} onChange={(e) => setFormData({ ...formData, dataImportacao: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono shadow-sm" />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Fornecedor</label>
            <input type="text" required placeholder="Ex: Samsung Electronics" value={formData.fornecedor} onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none shadow-sm" />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-semibold text-gray-700">Produto / Referência</label>
              <span className={`text-[10px] uppercase font-bold tracking-widest ${formData.produto?.length >= 250 ? 'text-red-500' : 'text-gray-400'}`}>
                {formData.produto?.length || 0}/255
              </span>
            </div>
            <textarea rows="2" maxLength={255} required placeholder="Ex: Lotes de Telas OLED" value={formData.produto} onChange={(e) => setFormData({ ...formData, produto: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none shadow-sm resize-none"></textarea>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">SKU</label>
            <input type="text" placeholder="Ex: SAM-OLED-123" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Moeda</label>
            <select value={formData.moeda} onChange={(e) => setFormData({ ...formData, moeda: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 shadow-sm">
              <option value="USD">Dólar (USD)</option>
              <option value="EUR">Euro (EUR)</option>
              <option value="CNY">Yuan (CNY)</option>
              <option value="BRL">Real (BRL)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">FOB Total</label>
            <input type="number" step="0.01" required placeholder="0.00" value={formData.valorInicial} onChange={(e) => setFormData({ ...formData, valorInicial: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Custo Projetado de Nacionalização</label>
            <input
              type="number"
              step="0.01"
              placeholder="Ex: 339035.13"
              value={formData.custoDesembaraco}
              onChange={(e) => setFormData({ ...formData, custoDesembaraco: e.target.value })}
              className="w-full px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-purple-900 shadow-sm"
            />
            <p className={`text-[11px] mt-1.5 font-bold ${formData.custoDesembaraco ? 'text-purple-600' : 'text-gray-500'}`}>
              {formData.custoDesembaraco
                ? '✅ Valor manual aplicado'
                : `⚡ Automático (50% do FOB BRL): R$ ${((Number(formData.valorInicial) || 0) * (Number(formData.taxaCambioEntrada) || 0) * 0.50).toFixed(2)}`
              }
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Cotação do Câmbio</label>
            <input type="number" step="0.0001" required placeholder="5.3600" value={formData.taxaCambioEntrada} onChange={(e) => setFormData({ ...formData, taxaCambioEntrada: e.target.value })} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono shadow-sm" />
          </div>
        </div>
      </div>

      {/* 🚢 2. BLoco Linha do Tempo e Datas */}
      <div className="bg-gray-50/80 p-6 rounded-2xl border border-gray-200 shadow-sm">
        <h3 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
          <FiCalendar className="text-emerald-500" /> 2. Linha do Tempo e Datas
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-3 rounded-lg border-l-4 border-emerald-500 shadow-sm">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1 flex items-center gap-1"><FiClock/> Data da Invoice</label>
            <input type="date" value={formData.dataInvoice} onChange={handleInvoiceChange} className="w-full p-1 bg-transparent focus:outline-none font-mono text-xs" />
          </div>
          
          <div className="bg-white p-3 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Pagamento (30%)</label>
            <input type="date" value={formData.dataPagamento30} onChange={(e) => setFormData({ ...formData, dataPagamento30: e.target.value })} className="w-full p-1 bg-transparent focus:outline-none font-mono text-xs" />
          </div>

          <div className="bg-white p-3 rounded-lg border-l-4 border-amber-400 shadow-sm">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Embarque (+50d)</label>
            <input type="date" value={formData.previsaoEmbarque} onChange={(e) => setFormData({ ...formData, previsaoEmbarque: e.target.value })} className="w-full p-1 bg-transparent focus:outline-none font-mono text-xs" />
          </div>

          <div className="bg-white p-3 rounded-lg border-l-4 border-orange-400 shadow-sm">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Pagamento (70%)</label>
            <input type="date" value={formData.dataPagamento70} onChange={(e) => setFormData({ ...formData, dataPagamento70: e.target.value })} className="w-full p-1 bg-transparent focus:outline-none font-mono text-xs" />
          </div>

          <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500 shadow-sm">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Chegada (+100d)</label>
            <input type="date" value={formData.previsaoChegada} onChange={(e) => setFormData({ ...formData, previsaoChegada: e.target.value })} className="w-full p-1 bg-transparent focus:outline-none font-mono text-xs" />
          </div>
        </div>
      </div>

      {/* 💰 3. BLoco Financiamento Entrada 30% */}
      <div className="bg-gray-50/80 p-6 rounded-2xl border border-gray-200 shadow-sm">
        <h3 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
          <FiCreditCard className="text-indigo-500" /> 3. Custos e Financiamento da Entrada (30%)
        </h3>
        
        <div className="mb-6 p-4 bg-indigo-50/50 rounded-lg border border-indigo-100">
          <p className="text-sm font-semibold text-indigo-900">Entrada 30% Estimada (R$)</p>
          <p className="text-2xl font-black text-indigo-700">{formatarMoeda((Number(formData.valorInicial) || 0) * (Number(formData.taxaCambioEntrada) || 0) * 0.30)}</p>
        </div>

        <div className="p-5 bg-indigo-50/50 rounded-xl border border-indigo-100">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <div className="relative">
              <input type="checkbox" className="sr-only" checked={formData.possuiFinimp30} onChange={(e) => setFormData({ ...formData, possuiFinimp30: e.target.checked })} />
              <div className={`block w-14 h-8 rounded-full transition-colors ${formData.possuiFinimp30 ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
              <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${formData.possuiFinimp30 ? 'transform translate-x-6' : ''}`}></div>
            </div>
            <span className="font-bold text-indigo-900">Financiar Entrada (Finimp 30%)?</span>
          </label>

          {formData.possuiFinimp30 && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 animate-in slide-in-from-top-4">
              <div>
                <label className="block text-xs font-semibold text-indigo-800 mb-1">Banco</label>
                <input type="text" placeholder="Ex: Itaú" value={formData.bancoFinimp30} onChange={(e) => setFormData({ ...formData, bancoFinimp30: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-indigo-800 mb-1">Contratação</label>
                <input type="date" value={formData.dataContratacaoFinimp30} onChange={(e) => setFormData({ ...formData, dataContratacaoFinimp30: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg font-mono text-sm shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-indigo-800 mb-1">Quitação</label>
                <input type="date" value={formData.dataQuitacaoFinimp30} onChange={(e) => setFormData({ ...formData, dataQuitacaoFinimp30: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg font-mono text-sm shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-indigo-800 mb-1">Juros a.m. (%)</label>
                <input type="number" step="0.001" required={formData.possuiFinimp30} placeholder="0.99" value={formData.taxaJurosMensal30} onChange={(e) => setFormData({ ...formData, taxaJurosMensal30: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg font-mono shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-indigo-800 mb-1">Prazo (Dias)</label>
                <input type="number" required={formData.possuiFinimp30} placeholder="Ex: 180" value={formData.prazoFinimp30Dias} onChange={(e) => setFormData({ ...formData, prazoFinimp30Dias: e.target.value })} className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg font-mono shadow-sm" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 💼 4. BLoco Financiamento Saldo 70% */}
      <div className="bg-gray-50/80 p-6 rounded-2xl border border-gray-200 shadow-sm">
        <h3 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
          <FiCreditCard className="text-orange-500" /> 4. Custos e Financiamento do Saldo (70%)
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="p-4 bg-orange-50/50 rounded-lg border border-orange-100">
            <p className="text-sm font-semibold text-orange-900">Saldo 70% Estimado (R$)</p>
            <p className="text-2xl font-black text-orange-700">{formatarMoeda((Number(formData.valorInicial) || 0) * (Number(formData.taxaCambioEntrada) || 0) * 0.70)}</p>
          </div>

        </div>

        <div className="p-5 bg-orange-50/50 rounded-xl border border-orange-100">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <div className="relative">
              <input type="checkbox" className="sr-only" checked={formData.possuiFinimp70} onChange={(e) => setFormData({ ...formData, possuiFinimp70: e.target.checked })} />
              <div className={`block w-14 h-8 rounded-full transition-colors ${formData.possuiFinimp70 ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
              <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${formData.possuiFinimp70 ? 'transform translate-x-6' : ''}`}></div>
            </div>
            <span className="font-bold text-orange-900">Financiar Saldo (Finimp 70%)?</span>
          </label>

          {formData.possuiFinimp70 && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 animate-in slide-in-from-top-4">
              <div>
                <label className="block text-xs font-semibold text-orange-800 mb-1">Banco</label>
                <input type="text" placeholder="Ex: Santander" value={formData.bancoFinimp70} onChange={(e) => setFormData({ ...formData, bancoFinimp70: e.target.value })} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-orange-800 mb-1">Contratação</label>
                <input type="date" value={formData.dataContratacaoFinimp70} onChange={(e) => setFormData({ ...formData, dataContratacaoFinimp70: e.target.value })} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg font-mono text-sm shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-orange-800 mb-1">Quitação</label>
                <input type="date" value={formData.dataQuitacaoFinimp70} onChange={(e) => setFormData({ ...formData, dataQuitacaoFinimp70: e.target.value })} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg font-mono text-sm shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-orange-800 mb-1">Juros a.m. (%)</label>
                <input type="number" step="0.001" required={formData.possuiFinimp70} placeholder="1.20" value={formData.taxaJurosMensal70} onChange={(e) => setFormData({ ...formData, taxaJurosMensal70: e.target.value })} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg font-mono shadow-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-orange-800 mb-1">Prazo (Dias)</label>
                <input type="number" required={formData.possuiFinimp70} placeholder="Ex: 360" value={formData.prazoFinimp70Dias} onChange={(e) => setFormData({ ...formData, prazoFinimp70Dias: e.target.value })} className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg font-mono shadow-sm" />
              </div>
            </div>
          )}
        </div>
      </div>

      {resultadoSimulacao && (
        <div className="space-y-6">
          {(formData.possuiFinimp30 || formData.possuiFinimp70) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in zoom-in duration-300">
              <div className="bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl p-6 shadow-lg text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10"></div>
                <div className="relative z-10">
                  <p className="text-blue-100 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 mb-1">
                    <FiDollarSign /> Principal Financiado Estimado
                  </p>
                  <p className="text-3xl font-black drop-shadow-md mb-3">
                    {formatarMoeda(principalFinimpBrl)}
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6 shadow-lg text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10"></div>
                <div className="flex justify-between items-start relative z-10">
                  <div>
                    <p className="text-orange-100 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 mb-1">
                      <FiPercent /> Total Juros Financiamento
                    </p>
                    <p className="text-3xl font-black drop-shadow-md">
                      {formatarMoeda(jurosTotalBrl)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-[80px] -mr-32 -mt-32"></div>
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h3 className="text-blue-400 uppercase text-xs font-black tracking-widest flex items-center gap-2">
                <FiTrendingUp /> Projeção Estratégica
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
              <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex flex-col justify-center">
                <p className="text-gray-400 text-xs font-bold uppercase mb-1 flex items-center gap-1"><FiDollarSign /> Pagamento da Entrada (Caixa)</p>
                <p className="text-2xl font-light text-white">{formatarMoeda(resultadoSimulacao.pago30 || resultadoSimulacao.antecipacao30)}</p>
              </div>
              <div className="bg-blue-600/20 rounded-lg p-4 border border-blue-500/30 flex flex-col justify-center">
                <p className="text-blue-300 text-xs font-bold uppercase mb-1">Custo Total da Operação</p>
                <p className="text-3xl font-black text-white">{formatarMoeda(resultadoSimulacao.metaExibicao || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Observações Operacionais</label>
        <textarea rows="2" value={formData.observacoes} onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"></textarea>
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-gray-100 mt-8">
        <div>
          {importacaoParaEditar && (
            <button type="button" onClick={handleDeletar} disabled={loading} className="px-5 py-2.5 rounded-xl font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 transition-all flex items-center gap-2">
              <FiTrash2 size={18} /> Excluir Operação
            </button>
          )}
        </div>
        <button type="submit" disabled={loading || isSimulando} className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all flex items-center gap-2 ${loading ? 'bg-gray-400' : importacaoParaEditar ? 'bg-orange-600' : 'bg-blue-600'}`}>
          {loading ? 'Processando...' : importacaoParaEditar ? 'Atualizar Importação' : 'Criar Importação'}
        </button>
      </div>

    </form>
  );
}